'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface FloatingCTAProps {
  isVisible?: boolean;
  onApplicationClick?: () => void;
  onChatClick?: () => void;
  className?: string;
}

const FloatingCTA = ({
  isVisible = true,
  onApplicationClick,
  onChatClick,
  className = ''
}: FloatingCTAProps) => {
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    const handleScroll = () => setIsScrolled(window.scrollY > 500);

    const chatTimer = setTimeout(() => setShowChat(true), 30000);

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(chatTimer);
    };
  }, []);

  const handleApplicationClick = () => {
    const el = document.getElementById('aplicar');
    if (el) {
      const pos = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
    if (onApplicationClick) onApplicationClick();
  };

  const handleChatClick = () => {
    window.open('https://wa.link/anasilvialara', '_blank', 'noopener,noreferrer');
    if (onChatClick) onChatClick();
  };

  if (!isVisible) return null;

  if (isMobile) {
    return (
      <div className={`fixed bottom-0 left-0 right-0 z-40 ${className}`}>
        <div className="bg-white/95 backdrop-blur-xl border-t border-[#E8EDF5] px-4 py-3 safe-area-bottom">
          <div className="flex gap-3">
            <button
              onClick={handleApplicationClick}
              className="flex-1 bg-[#0A1628] text-white font-display font-semibold py-3.5 px-5 rounded-xl hover:bg-[#1D6FE8] transition-all duration-250 flex items-center justify-center gap-2 text-sm"
            >
              <Icon name="ArrowRightIcon" size={16} />
              {t('cta.becomeAmbassador')}
            </button>
            <button
              onClick={handleChatClick}
              className="bg-[#F4F7FC] text-[#0A1628] p-3.5 rounded-xl hover:bg-[#EEF4FD] transition-colors flex items-center justify-center relative"
              title={t('cta.talkToAdvisor')}
            >
              <Icon name="ChatBubbleLeftRightIcon" size={20} />
              <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#16A34A] rounded-full border border-white" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-40 flex flex-col gap-3 ${className}`}>
      {showChat && isScrolled && (
        <button
          onClick={handleChatClick}
          className="bg-white text-[#0A1628] p-3.5 rounded-2xl shadow-card border border-[#E8EDF5] hover:border-[#1D6FE8]/30 hover:shadow-card-hover transition-all duration-350 flex items-center justify-center relative group"
          title={t('cta.talkToAdvisor')}
        >
          <Icon name="ChatBubbleLeftRightIcon" size={22} />
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#16A34A] rounded-full border border-white animate-pulse" />
          <div className="absolute right-full mr-3 bg-[#0A1628] text-white px-3 py-2 rounded-xl text-xs font-body whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-250 pointer-events-none">
            {t('cta.advisorAvailable')}
          </div>
        </button>
      )}

      {isScrolled && (
        <button
          onClick={handleApplicationClick}
          className="bg-[#0A1628] text-white font-display font-semibold py-3.5 px-6 rounded-2xl shadow-cta hover:bg-[#1D6FE8] hover:shadow-blue hover:-translate-y-0.5 transition-all duration-350 flex items-center gap-2 text-sm animate-pulse-gold"
        >
          <Icon name="ArrowRightIcon" size={16} />
          {t('cta.becomeAmbassador')}
        </button>
      )}
    </div>
  );
};

export default FloatingCTA;